import { accountsApi } from '../../../shared/api/accounts.api';
import { fetchPaidStats, paidForRow, type PaidStatsResponse } from '../../statistics/api/ads-paid-stats';
import type { PresentationMedia } from './presentation-media';

/**
 * Просмотры с таргета для публикаций презентации.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫМ ЗАПРОСОМ. Instagram платную часть не отдаёт вовсе: `views`
 * у медиа — чистая органика, это сверено с сеткой профиля на 264 постах.
 * Цифры рекламы живут в рекламном кабинете, и достаёт их edge-функция
 * (`/instagram/ads-paid-stats`), у которой есть токен системного пользователя.
 * Ходить туда с сайта, а не с бэкенда, пришлось потому, что маршрута к
 * рекламе у бэкенда нет, а заводить его — это деплой чужой функции.
 *
 * ПОЧЕМУ ОДНИМ ЧИСЛОМ. На слайде стоит «Просмотры», и клиенту важен итог, а
 * не то, какой частью охвата публикация обязана рекламе. Разбивка есть в
 * «Статистике публикаций» — там она и нужна, чтобы оценивать открутку.
 *
 * ПОЧЕМУ ТОЛЬКО ПРОСМОТРЫ. Охват складывать нельзя: аудитории органики и
 * рекламы пересекаются, и сумма дала бы число больше правды. Лайки и
 * комментарии реклама почти не приносит, а сохранения и отправки Marketing
 * API отдаёт неполно.
 */

/** Окно рекламы, если у публикаций не оказалось даты: кабинет за квартал. */
const FALLBACK_WINDOW_DAYS = 90;

export type PaidViewsDeps = {
  listAccounts: () => Promise<Array<{ id: string; username: string }>>;
  fetchPaid: (params: { accountId: string; from: Date; to: Date }) => Promise<PaidStatsResponse>;
};

const REAL: PaidViewsDeps = {
  listAccounts: async () => {
    const { accounts } = await accountsApi.getAll();
    return (accounts ?? []).map((a: { id: string; username: string }) => ({
      id: a.id,
      username: a.username ?? '',
    }));
  },
  fetchPaid: fetchPaidStats,
};

/**
 * Начало окна — дата самой ранней публикации.
 *
 * Конец функция ставит сама, сегодняшним днём: реклама часто крутится ещё
 * дни после выхода поста, и обрезка по концу периода теряла до 41%.
 */
function windowStart(media: PresentationMedia[]): Date {
  const dates = media
    .map((m) => (m.publishedAt ? new Date(m.publishedAt).getTime() : NaN))
    .filter((t) => Number.isFinite(t));
  if (dates.length === 0) {
    return new Date(Date.now() - FALLBACK_WINDOW_DAYS * 24 * 60 * 60 * 1000);
  }
  return new Date(Math.min(...dates));
}

/**
 * mediaId → просмотры с таргета. Публикации без рекламы в карту не попадают.
 *
 * Аккаунт нужен функции только чтобы убедиться, что спрашивающий имеет дело с
 * заведённым аккаунтом: карту рекламы она отдаёт целиком, по всему кабинету.
 * Поэтому берём первый аккаунт, который нашёлся по имени из публикаций.
 */
export async function fetchPaidViews(
  media: PresentationMedia[],
  deps: PaidViewsDeps = REAL,
): Promise<Map<string, number>> {
  const views = new Map<string, number>();
  if (media.length === 0) return views;

  const accounts = await deps.listAccounts();
  const idByName = new Map(accounts.map((a) => [a.username.toLowerCase(), a.id]));
  const accountId =
    media.map((m) => idByName.get(m.username.toLowerCase())).find(Boolean) ?? accounts[0]?.id;
  if (!accountId) return views;

  const answer = await deps.fetchPaid({
    accountId,
    from: windowStart(media),
    to: new Date(),
  });
  if (!answer.enabled) return views;

  for (const m of media) {
    const paid = paidForRow(answer, {
      mediaId: m.mediaId,
      permalink: `https://www.instagram.com/p/${m.shortcode}/`,
    });
    if (paid && paid.views > 0) views.set(m.mediaId, paid.views);
  }
  return views;
}
